import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider.jsx';
import { useBoardLocale } from '@/components/board_portal/useBoardLocale';
import { boardDisplay } from '@/components/board_portal/boardPresentation';
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { submitPublicBoardVote, verifyPublicBoardMotion } from '../api/voting'

const parseQuery = (search) => new URLSearchParams(search)


export default function PublicVotingPage() {
  const { t, locale, date } = useBoardLocale();
  const { clubCode, motionId } = useParams()
  const location = useLocation()
  const { setLocale } = useOptionalPrivanaI18n();
  const linkLocale = parseQuery(location.search).get('lang');
  useEffect(() => { if (linkLocale === 'es' || linkLocale === 'en-GB') setLocale(linkLocale); }, [linkLocale, setLocale]);
  const token = useMemo(() => parseQuery(location.search).get('token') || '', [location.search])
  const [otpCode, setOtpCode] = useState('')
  const [verified, setVerified] = useState(false)
  const [motion, setMotion] = useState(null)
  const [tallies, setTallies] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [voteStatus, setVoteStatus] = useState(null)

  const handleVerify = async () => {
    if (!clubCode || !motionId || !token || !otpCode) return
    setLoading(true)
    setError(null)
    try {
      const data = await verifyPublicBoardMotion({ clubCode, motionId, token, otpCode })
      setMotion(data.motion)
      setTallies(data.tallies)
      setVoteStatus(data.tallies?.viewer_vote || null)
      setVerified(true)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (vote) => {
    if (!clubCode || !motionId || !token || !otpCode) return
    setLoading(true)
    setError(null)
    try {
      const data = await submitPublicBoardVote({ clubCode, motionId, token, otpCode, vote })
      setTallies(data?.tallies || null)
      setVoteStatus(vote)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mp-voting mp-voting--public">
      <div className="mp-voting__header">
        <div>
          <p className="mp-eyebrow">{t('boardPortal.boardVotingAccess')}{' '}</p>
          <h1>{motion?.title || t('boardPortal.verifyYourVotingAccess')}</h1>
          {motion?.closes_at && <p className="mp-muted">{t('boardPortal.closes')}{' '}{date(motion.closes_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}</p>}
        </div>
      </div>

      {!token && (
        <div className="mp-card mp-voting__card">
          <p className="mp-muted">{t('boardPortal.thisVotingLinkIsMissingItsAccessToken')}{' '}</p>
        </div>
      )}

      {token && !verified && (
        <div className="mp-card mp-voting__card mp-voting__otp">
          <h2>{t('boardPortal.enterYourOneTimeCode')}{' '}</h2>
          <p className="mp-muted">{t('boardPortal.useTheCodeFromYourEmailToUnlockTheBallot')}{' '}</p>
          <input
            type="text"
            value={otpCode}
            onChange={(event) => setOtpCode(event.target.value)}
            placeholder={t('boardPortal.6DigitCode')}
            className="mp-voting__input"
          />
          <button type="button" className="mp-voting__cta" onClick={handleVerify} disabled={loading}>
            {loading ? t('boardPortal.verifying') : t('boardPortal.verify')}
          </button>
          {error && <p className="mp-voting__error">{t('boardPortal.unableToVerifyTheCodePleaseTryAgain')}{' '}</p>}
        </div>
      )}

      {verified && motion && (
        <>
          <div className="mp-card mp-voting__card">
            <h2>{t('boardPortal.motionDetails')}{' '}</h2>
            <p className="mp-muted">{t('boardPortal.opens')}{' '}{date(motion.opens_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}</p>
            <p className="mp-muted">{t('boardPortal.closes')}{' '}{date(motion.closes_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}</p>
            <p className="mp-muted">{t('boardPortal.votesRecorded')}{' '}{tallies?.total || 0}</p>
          </div>
          <div className="mp-card mp-voting__vote-panel">
            <h2>{t('boardPortal.castYourVote')}{' '}</h2>
            <div className="mp-voting__buttons">
              {['yes', 'no', 'abstain'].map((vote) => (
                <button
                  key={vote}
                  type="button"
                  className={`mp-voting__vote ${voteStatus === vote ? 'is-active' : ''}`}
                  onClick={() => handleVote(vote)}
                  disabled={loading}
                >
                  {boardDisplay(t, 'vote', vote)}
                </button>
              ))}
            </div>
            {error && <p className="mp-voting__error">{t('boardPortal.unableToRecordVotePleaseTryAgain')}{' '}</p>}
          </div>
        </>
      )}
    </div>
  )
}
